import React, { useState, useMemo } from 'react';
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  Radio,
  Select,
  MenuItem,
  Checkbox,
  CircularProgress,
} from '@material-ui/core';
import { Close as CloseIcon, ArrowBack as ArrowBackIcon, Check as CheckIcon } from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import { DateTime } from 'luxon';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { useEndpointFetch } from '../../../../../global/network/useEndpointFetch';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { FallbackAvatar } from '../../../../general/user-profile/components/Avatar';
import { MatchedCandidate } from './matching/MatchingPanel';
import {
  SaveActivityFetchVariables,
  SaveActivityFetchSessionVariables,
} from '../../../activity-management/activity-form-v2/activity-form-actions-v2';

const YOUTH_PROGRAM_ID = '360dcfda-7994-4c96-a996-bab1d78661e9';
const CONTACT_FORM_REPORT_TYPE_ID = '0e50addd-27be-4eb3-9029-8fc3b1dc4422';
const MENTOR_ROLE_ID = '3a957d71-6452-487b-8024-318992f585f4';

type SchedulingType = 'regular' | 'flexible';
type FrequencyType = 'weekly' | 'fortnightly' | 'monthly';

interface ApplicationActionsDialogProps {
  open: boolean;
  onClose: () => void;
  profileId: string;
  applicantName: string;
  applicantRoleId: string;
  matchedCandidate: MatchedCandidate;
  onSuccess: () => void;
}

const useStyles = makeStyles({
  dialogPaper: {
    width: 520,
    maxHeight: '90vh',
    borderRadius: 8,
  },
  header: {
    padding: '16px 24px 0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerStep1: {
    padding: '8px 16px',
    borderBottom: '1px solid #DCDCDC',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: 23,
    fontWeight: 550,
    color: '#545454',
    paddingLeft: 5,
  },
  subtitle: {
    fontSize: 14,
    color: '#646464',
    padding: '8px 24px 0 29px',
  },
  titleStep1: {
    fontSize: 16,
    fontWeight: 600,
    color: '#545454',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  cardList: {
    padding: '16px 24px 24px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 16,
  },
  templateCard: {
    border: '2px solid #DCDCDC',
    borderRadius: 8,
    padding: '16px 20px',
    cursor: 'pointer',
    background: '#fff',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    '&:hover': {
      borderColor: '#9E9E9E',
    },
  },
  templateCardSelected: {
    borderColor: '#796fff',
    background: 'rgba(121, 111, 255, 0.05)',
  },
  cardIcon: {
    width: 40,
    height: 40,
    borderRadius: 8,
    background: '#F2F2F2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  cardIconSelected: {
    background: '#796fff',
    '& svg': { fill: '#fff' },
  },
  cardTitle: {
    fontWeight: 600,
    fontSize: '0.95rem',
    color: '#545454',
  },
  cardDesc: {
    fontSize: '0.8rem',
    color: '#646464',
    marginTop: 2,
    lineHeight: 1.4,
  },
  footer: {
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
  },
  footerBordered: {
    borderTop: '1px solid #DCDCDC',
  },
  step1Content: {
    padding: 24,
  },
  templateBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    background: '#F2F2F2',
    borderRadius: 4,
    padding: '4px 10px',
    marginBottom: 24,
    fontSize: '0.8rem',
    color: '#646464',
  },
  fieldLabel: {
    fontSize: '0.75rem',
    color: '#646464',
    marginBottom: 6,
    fontWeight: 500,
  },
  sectionLabel: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#545454',
    marginBottom: 8,
  },
  teamList: {
    border: '1px solid #DCDCDC',
    borderRadius: 8,
    overflow: 'hidden',
    marginBottom: 20,
  },
  teamRow: {
    padding: '12px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '& + &': {
      borderTop: '1px solid #DCDCDC',
    },
  },
  roleBadge: {
    fontSize: '0.75rem',
    fontWeight: 600,
    borderRadius: 4,
    padding: '2px 8px',
  },
  radioCard: {
    border: '2px solid #DCDCDC',
    borderRadius: 8,
    padding: '14px 16px',
    cursor: 'pointer',
    background: '#fff',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: '#9E9E9E',
    },
  },
  radioCardSelected: {
    borderColor: '#796fff',
    background: 'rgba(121, 111, 255, 0.05)',
  },
  sessionList: {
    border: '1px solid #DCDCDC',
    borderRadius: 8,
    overflow: 'hidden',
  },
  sessionRow: {
    padding: '12px 16px',
    '& + &': {
      borderTop: '1px solid #DCDCDC',
    },
  },
  sessionName: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: '0.9rem',
    fontWeight: 500,
    color: '#545454',
  },
  sessionReport: {
    fontSize: '0.8rem',
    color: '#646464',
    marginLeft: 24,
    marginTop: 2,
  },
  badge: {
    fontSize: '0.7rem',
    color: '#646464',
    background: '#F2F2F2',
    borderRadius: 4,
    padding: '1px 6px',
    fontWeight: 400,
  },
});

export const ApplicationActionsDialog: React.FC<ApplicationActionsDialogProps> = ({
  open,
  onClose,
  profileId,
  applicantName,
  applicantRoleId,
  matchedCandidate,
  onSuccess,
}) => {
  const classes = useStyles();
  const { theme } = useCampfireTheme();
  const [step, setStep] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [activityName, setActivityName] = useState('');
  const [startDate, setStartDate] = useState(DateTime.local().toISODate());
  const [scheduling, setScheduling] = useState<SchedulingType>('flexible');
  const [frequency, setFrequency] = useState<FrequencyType>('fortnightly');
  const [endDate, setEndDate] = useState(
    DateTime.local()
      .plus({ years: 1 })
      .toISODate()
  );
  const [includeGoalSetting, setIncludeGoalSetting] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const apiUrl = useApiUrl();
  const saveActivity = useEndpointFetch<SaveActivityFetchVariables, { activityId: string }>('/vm/activity/save', {
    formData: true,
  });
  const completeProfile = useEndpointFetch<{ profileId: string; bypass?: boolean }, string>(
    '/vm/volunteer/profile/complete'
  );
  const enrolFetch = useCampfireFetch({ defer: true });

  const isMentor = applicantRoleId === MENTOR_ROLE_ID;
  const mentorName = isMentor ? applicantName : matchedCandidate.name;
  const participantName = isMentor ? matchedCandidate.name : applicantName;

  const defaultActivityName = useMemo(() => {
    return `${mentorName} & ${participantName}`;
  }, [mentorName, participantName]);

  const handleContinueToStep1 = () => {
    if (!activityName) {
      setActivityName(defaultActivityName);
    }
    setStep(1);
  };

  const handleClose = () => {
    setStep(0);
    setSelectedTemplate(null);
    setActivityName('');
    setStartDate(DateTime.local().toISODate());
    setScheduling('flexible');
    setFrequency('fortnightly');
    setEndDate(
      DateTime.local()
        .plus({ years: 1 })
        .toISODate()
    );
    setIncludeGoalSetting(true);
    onClose();
  };

  const buildRecurrences = () => {
    if (scheduling !== 'regular') return [];

    const weekInterval = frequency === 'weekly' ? 1 : frequency === 'fortnightly' ? 2 : 4;
    const startDt = DateTime.fromISO(startDate);
    const dayMap: Record<number, string> = { 1: 'MO', 2: 'TU', 3: 'WE', 4: 'TH', 5: 'FR', 6: 'SA', 7: 'SU' };
    const day = dayMap[startDt.weekday] || 'MO';

    return [
      {
        __typename: 'WeeklyRecurrenceType' as const,
        startDate,
        endDate,
        days: [day],
        weekInterval,
      },
    ];
  };

  const enrolVolunteer = (activityId: string, volunteerId: string) => {
    return enrolFetch.run({
      method: 'post',
      url: `${apiUrl}/vm/activity/enrolment/enrol`,
      data: {
        activityIds: [activityId],
        volunteerIds: [volunteerId],
      },
    });
  };

  const handleCreateMatch = async () => {
    setIsSubmitting(true);

    try {
      const profileRes = await completeProfile.run({ profileId });
      if (!profileRes.ok) throw new Error('Failed to complete applicant profile');
      const vol1Data = profileRes.data?.data;
      const volunteerId1 = typeof vol1Data === 'string' ? vol1Data : vol1Data?.volunteerId;

      const matchedProfileRes = await completeProfile.run({ profileId: matchedCandidate.profileId, bypass: true });
      if (!matchedProfileRes.ok) throw new Error('Failed to complete matched candidate profile');
      const vol2Data = matchedProfileRes.data?.data;
      const volunteerId2 = typeof vol2Data === 'string' ? vol2Data : vol2Data?.volunteerId;

      const sessions: SaveActivityFetchSessionVariables[] = [
        {
          name: 'Meeting',
          description: '',
          startTime: null,
          endTime: null,
          reportTypeId: CONTACT_FORM_REPORT_TYPE_ID,
          isFlexible: scheduling === 'flexible',
        },
      ];

      const activityData: SaveActivityFetchVariables = {
        programId: YOUTH_PROGRAM_ID,
        name: activityName || defaultActivityName,
        description: '',
        startDate,
        endDate: scheduling === 'regular' ? endDate : undefined,
        isRestrictedActivity: false,
        isHidden: false,
        maxTeam: null,
        hasOpenRoster: false,
        requireRosterTemplateApproval: false,
        allowCICO: null,
        allowIndividualShiftTimes: null,
        allowRosterComments: null,
        sessions,
        recurrences: buildRecurrences(),
      };

      const activityRes = await saveActivity.run(activityData);
      if (!activityRes.ok) throw new Error('Failed to create activity');
      const activityId = activityRes.data?.data?.activityId;

      if (activityId && volunteerId1) {
        await enrolVolunteer(activityId, volunteerId1);
      }
      if (activityId && volunteerId2) {
        await enrolVolunteer(activityId, volunteerId2);
      }

      onSuccess();
      handleClose();
    } catch {
      setIsSubmitting(false);
    }
  };

  const renderStep0 = () => (
    <>
      <Box className={classes.header}>
        <Typography className={classes.title}>Application Actions</Typography>
        <IconButton size='small' onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Typography className={classes.subtitle}>
        Select an activity template to create for this match. The template defines sessions, reports, and default
        settings.
      </Typography>

      <Box className={classes.cardList}>
        <Box
          className={`${classes.templateCard} ${selectedTemplate === 'youth' ? classes.templateCardSelected : ''}`}
          onClick={() => setSelectedTemplate('youth')}
        >
          <Box className={`${classes.cardIcon} ${selectedTemplate === 'youth' ? classes.cardIconSelected : ''}`}>
            <svg width='20' height='20' viewBox='0 0 24 24'>
              <path
                d='M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z'
                fill={selectedTemplate === 'youth' ? '#fff' : '#646464'}
              />
            </svg>
          </Box>
          <Box>
            <Typography className={classes.cardTitle}>Youth Mentoring</Typography>
            <Typography className={classes.cardDesc}>
              Fortnightly meeting contact form, 6-month review with child wellbeing index, goal setting
            </Typography>
          </Box>
        </Box>

        <Box
          className={`${classes.templateCard} ${selectedTemplate === 'blank' ? classes.templateCardSelected : ''}`}
          onClick={() => setSelectedTemplate('blank')}
        >
          <Box className={`${classes.cardIcon} ${selectedTemplate === 'blank' ? classes.cardIconSelected : ''}`}>
            <svg width='20' height='20' viewBox='0 0 24 24'>
              <path d='M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z' fill={selectedTemplate === 'blank' ? '#fff' : '#646464'} />
            </svg>
          </Box>
          <Box>
            <Typography className={classes.cardTitle}>Blank Activity</Typography>
            <Typography className={classes.cardDesc}>
              Start from scratch — no sessions or reports preconfigured
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box className={classes.footer}>
        <Button onClick={handleClose} style={{ color: '#d32f2f', textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant='contained'
          color='primary'
          disabled={!selectedTemplate}
          onClick={handleContinueToStep1}
          style={{ borderRadius: 48, textTransform: 'none' }}
        >
          Continue
        </Button>
      </Box>
    </>
  );

  const renderStep1 = () => {
    const startDt = DateTime.fromISO(startDate);
    const sixMonthDate = startDt.plus({ months: 6 }).toISODate();
    const twelveMonthDate = startDt.plus({ months: 12 }).toISODate();
    const isYouth = selectedTemplate === 'youth';

    return (
      <>
        <Box className={classes.headerStep1}>
          <Box className={classes.titleStep1}>
            <IconButton size='small' onClick={() => setStep(0)}>
              <ArrowBackIcon fontSize='small' />
            </IconButton>
            Create Match
          </Box>
          <IconButton size='small' onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={classes.step1Content}>
          <Box className={classes.templateBadge}>
            Template:&nbsp;<strong>{isYouth ? 'Youth Mentoring' : 'Blank Activity'}</strong>
          </Box>

          <Box mb='20px'>
            <Typography className={classes.fieldLabel}>ACTIVITY NAME</Typography>
            <TextField
              fullWidth
              variant='outlined'
              size='small'
              value={activityName || defaultActivityName}
              onChange={(e) => setActivityName(e.target.value)}
            />
          </Box>

          <Box mb='20px'>
            <Typography className={classes.fieldLabel}>START DATE</Typography>
            <TextField
              fullWidth
              variant='outlined'
              size='small'
              type='date'
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <Typography className={classes.sectionLabel}>Team</Typography>
          <Box className={classes.teamList}>
            <Box className={classes.teamRow}>
              <Box display='flex' alignItems='center' style={{ gap: 10 }}>
                <FallbackAvatar
                  initials={mentorName
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .slice(0, 2)}
                  size={32}
                />
                <Typography style={{ fontSize: '0.9rem', color: '#545454' }}>{mentorName}</Typography>
              </Box>
              <span className={classes.roleBadge} style={{ color: '#796fff', background: 'rgba(121, 111, 255, 0.07)' }}>
                Mentor
              </span>
            </Box>
            <Box className={classes.teamRow}>
              <Box display='flex' alignItems='center' style={{ gap: 10 }}>
                <FallbackAvatar
                  initials={participantName
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .slice(0, 2)}
                  size={32}
                />
                <Typography style={{ fontSize: '0.9rem', color: '#545454' }}>{participantName}</Typography>
              </Box>
              <span className={classes.roleBadge} style={{ color: '#ff9237', background: 'rgba(255, 146, 55, 0.07)' }}>
                Participant
              </span>
            </Box>
          </Box>

          <Typography className={classes.sectionLabel}>Scheduling</Typography>
          <Box display='flex' flexDirection='column' style={{ gap: 10, marginBottom: 20 }}>
            <Box
              className={`${classes.radioCard} ${scheduling === 'regular' ? classes.radioCardSelected : ''}`}
              onClick={() => setScheduling('regular')}
            >
              <Box display='flex' style={{ gap: 10 }}>
                <Radio
                  checked={scheduling === 'regular'}
                  size='small'
                  color='primary'
                  style={{ padding: 0, marginTop: 1 }}
                />
                <Box>
                  <Typography style={{ fontWeight: 600, fontSize: '0.9rem', color: '#545454' }}>Regular</Typography>
                  <Typography style={{ fontSize: '0.8rem', color: '#646464', lineHeight: 1.4 }}>
                    Pre-create rosters on a set schedule. Both team members are auto-rostered to each date.
                  </Typography>
                </Box>
              </Box>
              {scheduling === 'regular' && (
                <Box display='flex' style={{ gap: 12, marginTop: 14, marginLeft: 28 }}>
                  <Box flex={1}>
                    <Typography style={{ fontSize: '0.7rem', color: '#646464', marginBottom: 4, fontWeight: 500 }}>
                      FREQUENCY
                    </Typography>
                    <Select
                      fullWidth
                      variant='outlined'
                      value={frequency}
                      onChange={(e) => setFrequency(e.target.value as FrequencyType)}
                      style={{ fontSize: '0.85rem' }}
                      margin='dense'
                    >
                      <MenuItem value='weekly'>Weekly</MenuItem>
                      <MenuItem value='fortnightly'>Fortnightly</MenuItem>
                      <MenuItem value='monthly'>Monthly</MenuItem>
                    </Select>
                  </Box>
                  <Box flex={1}>
                    <Typography style={{ fontSize: '0.7rem', color: '#646464', marginBottom: 4, fontWeight: 500 }}>
                      END DATE
                    </Typography>
                    <TextField
                      fullWidth
                      variant='outlined'
                      size='small'
                      type='date'
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </Box>
                </Box>
              )}
            </Box>

            <Box
              className={`${classes.radioCard} ${scheduling === 'flexible' ? classes.radioCardSelected : ''}`}
              onClick={() => setScheduling('flexible')}
            >
              <Box display='flex' style={{ gap: 10 }}>
                <Radio
                  checked={scheduling === 'flexible'}
                  size='small'
                  color='primary'
                  style={{ padding: 0, marginTop: 1 }}
                />
                <Box>
                  <Typography style={{ fontWeight: 600, fontSize: '0.9rem', color: '#545454' }}>Flexible</Typography>
                  <Typography style={{ fontSize: '0.8rem', color: '#646464', lineHeight: 1.4 }}>
                    No pre-set dates. Mentor or coordinator adds meeting dates via the Rosters page as they go.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {isYouth && (
            <>
              <Typography className={classes.sectionLabel}>Sessions</Typography>
              <Box className={classes.sessionList}>
                <Box className={classes.sessionRow}>
                  <Box display='flex' justifyContent='space-between' alignItems='center'>
                    <Box className={classes.sessionName}>
                      <CheckIcon style={{ fontSize: 16, color: theme.palette.primary.main }} />
                      Meeting
                    </Box>
                    {scheduling === 'regular' && <span className={classes.badge}>recurring</span>}
                  </Box>
                  <Typography className={classes.sessionReport}>Report: Youth Meeting Contact Form</Typography>
                </Box>

                <Box className={classes.sessionRow}>
                  <Box display='flex' justifyContent='space-between' alignItems='center'>
                    <Box className={classes.sessionName}>
                      <CheckIcon style={{ fontSize: 16, color: theme.palette.primary.main }} />
                      6-Month Review
                    </Box>
                    <Typography style={{ fontSize: '0.8rem', color: '#646464' }}>{sixMonthDate}</Typography>
                  </Box>
                  <Typography className={classes.sessionReport}>Task: Youth 6-Month Review</Typography>
                </Box>

                <Box className={classes.sessionRow}>
                  <Box display='flex' justifyContent='space-between' alignItems='center'>
                    <Box className={classes.sessionName}>
                      <CheckIcon style={{ fontSize: 16, color: theme.palette.primary.main }} />
                      12-Month Review
                    </Box>
                    <Typography style={{ fontSize: '0.8rem', color: '#646464' }}>{twelveMonthDate}</Typography>
                  </Box>
                  <Typography className={classes.sessionReport}>Task: Youth 12-Month Review</Typography>
                </Box>

                <Box className={classes.sessionRow}>
                  <Box display='flex' justifyContent='space-between' alignItems='center'>
                    <Box className={classes.sessionName}>
                      <Checkbox
                        checked={includeGoalSetting}
                        onChange={(e) => setIncludeGoalSetting(e.target.checked)}
                        size='small'
                        color='primary'
                        style={{ padding: 0 }}
                      />
                      Goal Setting
                    </Box>
                  </Box>
                  <Typography className={classes.sessionReport}>Report: Youth Goal Form</Typography>
                </Box>
              </Box>
            </>
          )}
        </Box>

        <Box className={`${classes.footer} ${classes.footerBordered}`}>
          <Button onClick={handleClose} style={{ color: '#d32f2f', textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={handleCreateMatch}
            disabled={isSubmitting}
            style={{ borderRadius: 48, textTransform: 'none' }}
            startIcon={isSubmitting ? <CircularProgress size={16} color='inherit' /> : undefined}
          >
            {isSubmitting ? 'Creating...' : 'Create Match'}
          </Button>
        </Box>
      </>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} PaperProps={{ className: classes.dialogPaper }} maxWidth={false}>
      {step === 0 ? renderStep0() : renderStep1()}
    </Dialog>
  );
};
